import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { type ReactNode, Suspense } from "react";
import { PROJECT } from "@/app/capabilities/permissions";
import {
  GatewayCreatePage,
  GatewayFormSkeleton,
} from "@/app/mcp-gateways/create-page/gateway-create-page";
import { gatewayFormTitle } from "@/app/mcp-gateways/create-page/gateway-form-access";
import {
  mcpGatewayNewSearchDefaults,
  mcpGatewayNewSearchSchema,
} from "@/app/mcp-gateways/search-params";
import { toolkitCollectionQueryOptions } from "@/app/tools/api/get-toolkit-collection";
import { useToolkitCatalog } from "@/hooks/use-toolkit-catalog";
import { requireProjectPermission } from "@/lib/route-guards";

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId/mcp-gateways/new"
)({
  validateSearch: mcpGatewayNewSearchSchema,
  search: {
    middlewares: [stripSearchParams(mcpGatewayNewSearchDefaults)],
  },
  beforeLoad: (ctx) =>
    requireProjectPermission(ctx, PROJECT.mcpGateways.create),
  loader: ({ context: { queryClient }, params }) => {
    queryClient.prefetchQuery(
      toolkitCollectionQueryOptions({
        organizationId: params.organizationId,
        projectId: params.projectId,
      })
    );
    return { crumb: "New gateway" };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { tools } = Route.useSearch();
  if (tools.length === 0) {
    return <GatewayCreatePage initialTools={tools} />;
  }
  // Prefilled `?tools=` must be reconciled against the catalog (canonical
  // casing, version stripping, dropping unknowns) before a submit can fire, so
  // the form mounts only once the catalog is present.
  return (
    <Suspense
      fallback={<GatewayFormSkeleton title={gatewayFormTitle(false, false)} />}
    >
      <WithToolkitCatalog>
        <GatewayCreatePage initialTools={tools} />
      </WithToolkitCatalog>
    </Suspense>
  );
}

function WithToolkitCatalog({ children }: { children: ReactNode }) {
  useToolkitCatalog();
  return children;
}
